// ===========================================
// PREMIUM SIDEBAR - VP-Level Design
// Matching Tripnovac Platform Hub Quality
// Dark Theme with Elegant Navigation
// ===========================================

import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getApiBaseUrl } from '../../lib/apiBase';
import {
  LayoutDashboard,
  Users,
  Mail,
  Megaphone,
  BarChart3,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plane,
  Globe,
  Sparkles,
  CreditCard,
  FileText,
  Calendar,
  UserCog,
  Target,
  MessageSquare,
  Share2,
  Zap,
  PanelTop,
  Bell,
  Search,
  Command,
  HelpCircle,
  Book,
  ExternalLink,
  Moon,
  Sun,
  Bot,
  Code,
  Building2,
  DollarSign,
} from 'lucide-react';
import { useUIStore, useAuthStore } from '../../store';
import { toast } from 'react-hot-toast';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  badge?: number | string;
  badgeColor?: 'violet' | 'emerald' | 'amber' | 'red';
  children?: NavItem[];
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const PremiumSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarCollapsed, setSidebarCollapsed, toggleDarkMode, darkMode } = useUIStore();
  const { logout, user } = useAuthStore();
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['marketing']);
  const [counts, setCounts] = useState({ contacts: 0, leads: 0, campaigns: 0 });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token || !user) return; // Wait for auth
        const headers = { Authorization: `Bearer ${token}` };
        const apiUrl = getApiBaseUrl();
        const [contactsRes, leadsRes, campaignsRes] = await Promise.allSettled([
          fetch(`${apiUrl}/api/contacts?limit=1`, { headers }),
          fetch(`${apiUrl}/api/leads?limit=1`, { headers }),
          fetch(`${apiUrl}/api/campaigns?limit=1`, { headers }),
        ]);
        const parse = async (r: PromiseSettledResult<Response>) => {
          if (r.status === 'fulfilled' && r.value.ok) {
            const json = await r.value.json();
            // API returns: { success, data: { contacts/leads/campaigns, total, ... } } or { total, pagination: { total } }
            return json?.data?.total ?? json?.pagination?.total ?? json?.total ?? 0;
          }
          return 0;
        };
        setCounts({
          contacts: await parse(contactsRes),
          leads: await parse(leadsRes),
          campaigns: await parse(campaignsRes),
        });
      } catch { /* silently fail — badges just show 0 */ }
    };
    fetchCounts();
  }, [user]);

  const navSections: NavSection[] = [
    {
      title: 'Overview',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
        { path: '/analytics', label: 'Analytics', icon: <BarChart3 className="w-5 h-5" /> },
        { path: '/messages', label: 'Messages', icon: <MessageSquare className="w-5 h-5" /> },
        { path: '/notifications', label: 'Notifications', icon: <Bell className="w-5 h-5" /> },
      ],
    },
    {
      title: 'CRM',
      items: [
        { path: '/contacts', label: 'Contacts', icon: <Users className="w-5 h-5" />, badge: counts.contacts > 0 ? (counts.contacts >= 1000 ? `${(counts.contacts/1000).toFixed(1)}K` : counts.contacts) : undefined },
        { path: '/leads', label: 'Leads', icon: <Target className="w-5 h-5" />, badge: counts.leads > 0 ? counts.leads : undefined, badgeColor: 'emerald' as const },
        { path: '/bookings', label: 'Bookings', icon: <Calendar className="w-5 h-5" /> },
        { path: '/commissions', label: 'Commissions', icon: <DollarSign className="w-5 h-5" /> },
        { path: '/referrals', label: 'Referrals', icon: <Share2 className="w-5 h-5" /> },
      ],
    },
    {
      title: 'Marketing',
      items: [
        { path: '/campaigns', label: 'Campaigns', icon: <Megaphone className="w-5 h-5" />, badge: counts.campaigns > 0 ? counts.campaigns : undefined, badgeColor: 'violet' as const },
        { path: '/emails', label: 'Email Templates', icon: <Mail className="w-5 h-5" /> },
        { path: '/automation', label: 'Automation', icon: <Zap className="w-5 h-5" /> },
        { path: '/sms', label: 'SMS Marketing', icon: <MessageSquare className="w-5 h-5" /> },
        { path: '/whatsapp', label: 'WhatsApp', icon: <MessageSquare className="w-5 h-5" /> },
        { path: '/social', label: 'Social Media', icon: <Share2 className="w-5 h-5" /> },
        { path: '/landing-pages', label: 'Landing Pages', icon: <PanelTop className="w-5 h-5" /> },
      ],
    },
    {
      title: 'Finance',
      items: [
        { path: '/invoices', label: 'Invoices', icon: <FileText className="w-5 h-5" /> },
        { path: '/payments', label: 'Payments', icon: <CreditCard className="w-5 h-5" /> },
      ],
    },
    {
      title: 'AI & Tools',
      items: [
        { path: '/ai', label: 'AI Assistant', icon: <Bot className="w-5 h-5" />, badge: 'AI', badgeColor: 'violet' },
        { path: '/api-docs', label: 'API Docs', icon: <Code className="w-5 h-5" /> },
      ],
    },
    {
      title: 'Settings',
      items: [
        { path: '/users', label: 'Team', icon: <UserCog className="w-5 h-5" /> },
        { path: '/settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> },
      ],
    },
  ];

  const toggleGroup = (title: string) => {
    setExpandedGroups(prev =>
      prev.includes(title)
        ? prev.filter(g => g !== title)
        : [...prev, title]
    );
  };

  const NavItemComponent = ({ item }: { item: NavItem }) => {
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedGroups.includes(item.path);

    const badgeColors = {
      violet: 'bg-violet-500 text-white',
      emerald: 'bg-emerald-500 text-white',
      amber: 'bg-amber-500 text-white',
      red: 'bg-red-500 text-white',
    };

    return (
      <div>
        <NavLink
          to={hasChildren ? '#' : item.path}
          onClick={(e) => {
            if (hasChildren) {
              e.preventDefault();
              toggleGroup(item.path);
            }
          }}
          className={`
            group flex items-center gap-3 px-4 py-3 rounded-xl mx-2
            transition-all duration-200 ease-out relative overflow-hidden
            ${isActive
              ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-600/30'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
            }
            ${sidebarCollapsed ? 'justify-center mx-3' : ''}
          `}
        >
          {/* Active Indicator */}
          {isActive && !sidebarCollapsed && (
            <motion.div
              className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
          )}

          <span className={`flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-violet-400'}`}>
            {item.icon}
          </span>

          {!sidebarCollapsed && (
            <>
              <span className="font-medium flex-1">{item.label}</span>
              
              {item.badge && (
                <span className={`
                  px-2 py-0.5 text-xs font-bold rounded-full
                  ${isActive
                    ? 'bg-white/20 text-white'
                    : badgeColors[item.badgeColor || 'violet']
                  }
                `}>
                  {item.badge}
                </span>
              )}

              {hasChildren && (
                <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              )}
            </>
          )}
        </NavLink>

        {/* Children */}
        {hasChildren && !sidebarCollapsed && (
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="ml-8 mt-1 space-y-1"
              >
                {item.children?.map((child) => (
                  <NavItemComponent key={child.path} item={child} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`
        fixed left-0 top-0 h-screen
        bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950
        transition-all duration-300 ease-out z-50
        flex flex-col
        ${sidebarCollapsed ? 'w-20' : 'w-72'}
      `}
    >
      {/* Decorative Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTAgMGg0MHY0MEgweiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        <div className="absolute -top-24 -left-24 w-48 h-48 bg-violet-600/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-purple-600/10 rounded-full blur-3xl" />
      </div>

      {/* Logo Section */}
      <div className="relative p-5 border-b border-white/5">
        <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-4'}`}>
          {/* Tripnovac Nameplate + Sales & Marketing */}
          {!sidebarCollapsed ? (
            <div className="w-full flex flex-col items-center">
              {/* Nameplate Image */}
              <img 
                src="/assets/tripnovac-nameplate.jpeg" 
                alt="Tripnovac" 
                className="w-full h-auto object-contain"
                style={{ maxHeight: '48px' }}
              />
              {/* Sales & Marketing below nameplate */}
              <p className="text-sm font-medium tracking-wide mt-2 bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 bg-clip-text text-transparent">
                Sales & Marketing
              </p>
            </div>
          ) : (
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="relative"
            >
              <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg shadow-violet-600/30">
                <img 
                  src="/assets/sales-logo.jpeg" 
                  alt="Tripnovac Sales" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          )}
        </div>

        {/* Platform Status */}
        {!sidebarCollapsed && (
          <div className="mt-5 p-3 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-xs font-medium text-gray-400">Sales workspace</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Search */}
      {!sidebarCollapsed && (
        <div className="px-4 py-3">
          <button
            type="button"
            onClick={() => toast('Command search is coming soon. Use the sidebar to jump between modules.')}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all group"
          >
            <Search className="w-4 h-4" />
            <span className="text-sm">Search...</span>
            <div className="ml-auto flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 text-[10px] bg-white/10 rounded font-mono">⌘</kbd>
              <kbd className="px-1.5 py-0.5 text-[10px] bg-white/10 rounded font-mono">K</kbd>
            </div>
          </button>
        </div>
      )}

      {/* Main Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden custom-scrollbar">
        {navSections.map((section, sectionIndex) => (
          <div key={section.title} className={sectionIndex > 0 ? 'mt-6' : ''}>
            {!sidebarCollapsed && (
              <div className="px-6 mb-2">
                <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest">
                  {section.title}
                </span>
              </div>
            )}
            <div className="space-y-1">
              {section.items.map((item) => (
                <NavItemComponent key={item.path} item={item} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User Profile Section */}
      <div className="relative p-4 border-t border-white/5">
        {!sidebarCollapsed && user && (
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="flex items-center gap-3 px-4 py-3 mb-3 rounded-xl bg-gradient-to-r from-violet-600/20 to-purple-600/20 border border-violet-500/20 cursor-pointer"
            onClick={() => navigate('/settings')}
          >
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold shadow-lg">
                {user.firstName?.[0] || user.email[0].toUpperCase()}
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-gray-900" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">
                {user.firstName} {user.lastName}
              </p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className={`flex ${sidebarCollapsed ? 'flex-col items-center' : 'items-center'} gap-2`}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => logout()}
            className={`
              flex items-center gap-3 px-4 py-2.5 rounded-xl
              text-gray-400 hover:text-red-400 hover:bg-red-500/10
              transition-all duration-200
              ${sidebarCollapsed ? 'justify-center w-12' : 'flex-1'}
            `}
          >
            <LogOut className="w-5 h-5" />
            {!sidebarCollapsed && <span className="font-medium">Logout</span>}
          </motion.button>

          {!sidebarCollapsed && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                toggleDarkMode();
                const isDark = !darkMode;
                if (isDark) {
                  document.documentElement.classList.add('dark');
                } else {
                  document.documentElement.classList.remove('dark');
                }
              }}
              className="p-2.5 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-all"
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </motion.button>
          )}
        </div>
      </div>

      {/* Collapse Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-800 border border-gray-700 shadow-xl flex items-center justify-center text-gray-400 hover:text-white hover:bg-violet-600 hover:border-violet-500 transition-all duration-300 z-50"
      >
        {sidebarCollapsed ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}
      </motion.button>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      `}</style>
    </aside>
  );
};

export default PremiumSidebar;
